import { useState } from 'react';
import { useAdminQuotes } from '@/hooks/use-admin-quotes';
import { useProviderData } from '@/components/admin/peo/hooks/useProviderData';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/utils';
import { PlusCircle, Trash2, FileText, Info, Loader2, Building2, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';
import { EnterpriseQuoteBuilder } from '@/components/quotes/comparison/EnterpriseQuoteBuilder';

export const QuoteManagement = () => {
  const { 
    allQuotes, 
    isLoading, 
    addQuote, 
    isAddingQuote, 
    removeQuote, 
    isDeletingQuote,
    availableApplications,
    isLoadingApplications 
  } = useAdminQuotes();
  const { providers } = useProviderData();
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEnhancedBuilderOpen, setIsEnhancedBuilderOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('');
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [providerSearch, setProviderSearch] = useState('');
  const [activeTab, setActiveTab] = useState('simple');
  const filteredProviders = providers.filter(provider => 
    provider.name.toLowerCase().includes(providerSearch.toLowerCase())
  );
  
  const [formData, setFormData] = useState({
    applicationId: '',
    providerId: '',
    summary: '',
    adminNotes: ''
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleAddQuote = async () => {
    if (!formData.applicationId || !formData.providerId || !formData.summary) {
      return;
    }
    
    try {
      await addQuote({
        applicationId: formData.applicationId,
        providerId: formData.providerId,
        summary: formData.summary,
        adminNotes: formData.adminNotes || ''
      });
      
      setIsAddDialogOpen(false);
      setFormData({
        applicationId: '',
        providerId: '',
        summary: '',
        adminNotes: ''
      });
    } catch (error) {
      console.error("Failed to add quote:", error);
    }
  };
  
  const handleDeleteQuote = () => {
    if (selectedQuoteId) {
      removeQuote(selectedQuoteId);
      setIsDeleteDialogOpen(false);
      setSelectedQuoteId(null);
    }
  };
  
  const confirmDelete = (quoteId: string) => {
    setSelectedQuoteId(quoteId);
    setIsDeleteDialogOpen(true);
  };

  const handleEnhancedQuoteCreate = (applicationId: string, providerId: string) => {
    setSelectedApplicationId(applicationId);
    setSelectedProviderId(providerId);
    setIsEnhancedBuilderOpen(true);
  };

  const handleEnhancedQuoteSave = (quote: any) => {
    console.log('Enhanced quote saved:', quote);
    // This would integrate with the enhanced quote saving logic
  };

  const handleEnhancedQuoteSubmit = (quoteId: string) => {
    console.log('Enhanced quote submitted:', quoteId);
    setIsEnhancedBuilderOpen(false);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="w-8 h-8 border-t-2 border-primary rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">PEO Quotes</h2>
          <p className="text-muted-foreground">Manage quotes for applications with enhanced features</p>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <PlusCircle className="mr-2 h-4 w-4" />
                Simple Quote
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Quote</DialogTitle>
              <DialogDescription>
                Assign a quote to a PEO provider for an application
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="applicationId">Application</Label>
                  <Select 
                    onValueChange={(value) => handleSelectChange('applicationId', value)}
                    value={formData.applicationId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select application" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingApplications ? (
                        <SelectItem value="loading" disabled>Loading applications...</SelectItem>
                      ) : availableApplications.length > 0 ? (
                        availableApplications.map((app) => (
                          <SelectItem key={app.id} value={app.id}>
                            {app.company_legal_name || `${app.first_name} ${app.last_name}`}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>No available applications</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {!isLoadingApplications && availableApplications.length === 0 && (
                    <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                      <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <p>No applications available. Applications with accepted quotes are excluded to prevent duplicates.</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="providerId">PEO Provider</Label>
                  <Select 
                    onValueChange={(value) => handleSelectChange('providerId', value)}
                    value={formData.providerId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-2">
                        <Input
                          placeholder="Search providers..."
                          value={providerSearch}
                          onChange={(e) => setProviderSearch(e.target.value)}
                          className="mb-2"
                        />
                      </div>
                      {filteredProviders.length > 0 ? (
                        filteredProviders.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            {provider.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>No providers found</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="summary">Quote Summary</Label>
                <Textarea
                  id="summary"
                  name="summary"
                  value={formData.summary}
                  onChange={handleInputChange}
                  placeholder="Summarize key points of the quote"
                  className="min-h-[100px]"
                />
              </div>
              
              <div>
                <Label htmlFor="adminNotes">Admin Notes (Internal)</Label>
                <Textarea
                  id="adminNotes"
                  name="adminNotes"
                  value={formData.adminNotes}
                  onChange={handleInputChange}
                  placeholder="Notes visible only to admins"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddQuote} 
                disabled={isAddingQuote || !formData.applicationId || !formData.providerId || !formData.summary}
              >
                {isAddingQuote && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isAddingQuote ? "Adding..." : "Add Quote"}
              </Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
          
          <Button onClick={() => setActiveTab('enhanced')}>
            <Building2 className="mr-2 h-4 w-4" />
            Enhanced Quote Builder
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="simple">Simple Quote Management</TabsTrigger>
          <TabsTrigger value="enhanced">Enhanced Quote Builder</TabsTrigger>
        </TabsList>
        
        <TabsContent value="simple" className="space-y-4">
          {allQuotes.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center min-h-[200px] py-8">
                <FileText className="h-12 w-12 mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-1">No quotes available</h3>
                <p className="text-muted-foreground">
                  Add quotes for applications using the quote management tools above.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Table>
              <TableCaption>List of all uploaded PEO quotes</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Application</TableHead>
                  <TableHead>Monthly Cost</TableHead>
                  <TableHead>Annual Cost</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allQuotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="font-medium">{quote.provider_name}</TableCell>
                    <TableCell>{quote.company_name || quote.applicant_name}</TableCell>
                    <TableCell>{quote.monthly_cost ? formatCurrency(quote.monthly_cost) : '-'}</TableCell>
                    <TableCell>{quote.annual_cost ? formatCurrency(quote.annual_cost) : '-'}</TableCell>
                    <TableCell>{quote.status}</TableCell>
                    <TableCell>
                      {quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(quote.quote_file_url, '_blank')}
                          disabled={!quote.quote_file_url}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEnhancedQuoteCreate(quote.application_id, quote.provider_id)}
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => confirmDelete(quote.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
        
        <TabsContent value="enhanced" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Enhanced Quote Builder
              </CardTitle>
              <CardDescription>
                Create detailed quotes with line items, plan options, and implementation timelines
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label>Select Application</Label>
                  <Select value={selectedApplicationId} onValueChange={setSelectedApplicationId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose application" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableApplications.map((app) => (
                        <SelectItem key={app.id} value={app.id}>
                          {app.company_legal_name || `${app.first_name} ${app.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Select Provider</Label>
                  <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {selectedApplicationId && selectedProviderId && (
                <EnterpriseQuoteBuilder
                  applicationId={selectedApplicationId}
                  providerId={selectedProviderId}
                  onSave={handleEnhancedQuoteSave}
                  onSubmit={handleEnhancedQuoteSubmit}
                />
              )}
              
              {(!selectedApplicationId || !selectedProviderId) && (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select an application and provider to start building an enhanced quote</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this quote? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteQuote}
              disabled={isDeletingQuote}
            >
              {isDeletingQuote ? "Deleting..." : "Delete Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
